// Android Foundation: pila de "qué debe cerrar el botón Back físico primero". `Modal.tsx` empuja
// un handler mientras está abierto; el listener nativo en `App.tsx` cierra el modal más reciente
// antes de dejar que Capacitor navegue el historial del WebView o salga de la app — así un back
// accidental durante un flujo con modal abierto (p.ej. confirmar "DESCONTAR"/"REVERSAR"/"ANULAR")
// no lo salta sin cerrarlo.
type CloseHandler = () => void;

const stack: CloseHandler[] = [];

/**
 * `contains` (opcional) dice si este overlay CONTIENE a otro ya registrado. Un overlay nunca queda por
 * encima de los que contiene: si alguno ya está en la pila, entra justo debajo del primero. Hace falta
 * porque, cuando un diálogo y otro que vive dentro de él aparecen a la vez, el de dentro se registra
 * antes; por orden de llegada el de fuera quedaría arriba y el Back lo cerraría a él, llevándose al
 * de dentro aunque estuviera ocupado. Sin `contains`, el orden es el de llegada.
 */
export function pushModalClose(handler: CloseHandler, contains?: (other: CloseHandler) => boolean): () => void {
  const firstInside = contains ? stack.findIndex(contains) : -1;
  if (firstInside === -1) stack.push(handler);
  else stack.splice(firstInside, 0, handler);
  return () => {
    const index = stack.lastIndexOf(handler);
    if (index !== -1) stack.splice(index, 1);
  };
}

export function closeTopmostModal(): boolean {
  const handler = stack[stack.length - 1];
  if (!handler) return false;
  handler();
  return true;
}

/**
 * ¿Es este handler el overlay de más arriba? Lo usa Escape para respetar el mismo orden que el Back:
 * con un diálogo abierto encima de otro, cada uno escucha el teclado por su cuenta, y sin esta
 * pregunta un solo Escape cerraba los dos.
 */
export function isTopmostModalClose(handler: CloseHandler): boolean {
  return stack.length > 0 && stack[stack.length - 1] === handler;
}

/**
 * Fix HIGH-1 (revisión independiente): Back físico debe respetar exactamente la misma semántica de
 * bloqueo que Escape, el backdrop y el botón "×" — los tres ya se deshabilitan mientras `isBusy`
 * (ver `Modal.tsx`). Antes de este fix, `Modal.tsx` solo empujaba un handler cuando `!isBusy`, así
 * que un modal ocupado no estaba en la pila y el Back cruzaba directo a navegar/salir.
 *
 * `isBusy` es la ÚNICA fuente de verdad (la misma prop que ya usan Escape/backdrop/×) — esta función
 * no introduce ningún estado propio, solo decide qué handler corresponde según ese mismo valor.
 * Con `isBusy=true` se registra un no-op: el Back queda "consumido" (no cae a navegar/salir) pero no
 * cierra nada, igual que Escape/backdrop/× no hacen nada mientras el modal está ocupado.
 */
export function resolveBackButtonHandler(isBusy: boolean, onClose: CloseHandler): CloseHandler {
  return isBusy ? noop : onClose;
}

function noop(): void {}
